import { useLazyQuery } from '@apollo/client';

import Pagination, { defaultPagination } from '../../../../models/Pagination';
import Boutique, { BoutiqueRef } from '../../models/Boutique';
import { GET_BOUTIQUES } from './query';

interface FindBoutiquesInput {
  limit: number;
  offset: number;
  sortBy: string;
  sortDirection: string;
  searchText?: string;
  countries?: string[];
}

interface QueryData {
  result: {
    data: BoutiqueRef[];
    pagination: Pagination;
  };
}

interface QueryVars {
  input: FindBoutiquesInput;
}

export interface PaginatedBoutiques {
  boutiques: Boutique[];
  pagination: Pagination;
}

function getBoutiques(data: QueryData | undefined): Boutique[] {
  return data?.result.data.map((d) => d.value) ?? [];
}

function getPagination(data: QueryData | undefined): Pagination {
  return data?.result.pagination ?? defaultPagination;
}

/**
 * Fetch paginated list of boutiques
 */
function useFetchBoutiques() {
  const [execute, { loading, error, data, refetch, called, client }] = useLazyQuery<QueryData, QueryVars>(
    GET_BOUTIQUES,
    {
      notifyOnNetworkStatusChange: true,
    },
  );

  const fetchBoutiques = async ({
    sortBy,
    sortDirection,
    ...params
  }: FindBoutiquesInput): Promise<PaginatedBoutiques> => {
    const variables: QueryVars = {
      input: {
        ...params,
        sortBy: sortBy === 'country' ? 'countryId' : sortBy,
        sortDirection: sortDirection.toUpperCase(),
      },
    };

    if (!called) {
      // TODO GMB see if it's necessary to evict the cache (instead of returned the updated fields from the query)
      await client.refetchQueries({
        updateCache(cache) {
          cache.evict({ fieldName: 'findBoutiquesQuery' });
        },
      });
    }

    const response = called
      ? await refetch(variables)
      : await execute({
          variables,
        });

    return {
      boutiques: getBoutiques(response.data),
      pagination: getPagination(response.data),
    };
  };

  return {
    fetchBoutiques,
    loading,
    error,
    boutiques: getBoutiques(data),
    pagination: getPagination(data),
  };
}

export default useFetchBoutiques;
